import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { Section, Eyebrow } from "@/components/Section";
import { plans, type PlanId } from "@/data/pricing";
import { cn } from "@/lib/cn";

function isPlanId(value: string | null): value is PlanId {
  return value === "starter" || value === "professional" || value === "enterprise";
}

export function SignupPage() {
  const [params] = useSearchParams();
  const selected = useMemo<PlanId>(() => {
    const fromQuery = params.get("plan");
    return isPlanId(fromQuery) ? fromQuery : "professional";
  }, [params]);

  return (
    <>
      <Head
        title="Sign up for ServiTrex"
        description="Choose a ServiTrex plan and continue to app.servitrex.com to create your account and complete checkout."
      />

      <Section className="pt-14 sm:pt-16">
        <Eyebrow>Sign up</Eyebrow>
        <h1 className="max-w-3xl text-balance text-4xl sm:text-5xl">
          Pick a plan, then finish account setup in the app.
        </h1>
        <p className="mt-5 max-w-2xl text-pretty text-lg text-muted">
          Checkout and workspace provisioning happen on{" "}
          <span className="font-semibold text-fg">app.servitrex.com</span>. This page
          simply routes you to the right plan.
        </p>

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {plans.map((plan) => {
            const active = plan.id === selected;
            const href =
              plan.id === "enterprise"
                ? "/demo"
                : `https://app.servitrex.com/signup?plan=${plan.id}`;

            return (
              <article
                key={plan.id}
                className={cn(
                  "flex h-full flex-col rounded-[1.75rem] border border-border bg-surface-raised p-6",
                  active && "border-teal/50 shadow-glow",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-2xl tracking-tight">{plan.name}</h2>
                    <p className="mt-2 text-sm text-muted">
                      {plan.price}
                      {plan.id !== "enterprise" ? "/mo" : ""}
                    </p>
                  </div>
                  {active ? (
                    <span className="rounded-full bg-teal/15 px-3 py-1 text-xs font-semibold text-teal-deep">
                      Selected
                    </span>
                  ) : null}
                </div>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-muted">
                  {plan.description}
                </p>
                {plan.id === "enterprise" ? (
                  <Button to={href} className="mt-6" variant={active ? "primary" : "secondary"}>
                    Talk to sales
                  </Button>
                ) : (
                  <Button
                    href={href}
                    className="mt-6"
                    variant={active ? "primary" : "secondary"}
                  >
                    Continue with {plan.name}
                  </Button>
                )}
              </article>
            );
          })}
        </div>

        <p className="mt-8 text-sm text-muted">
          Need a walkthrough first?{" "}
          <Link className="font-semibold text-teal-deep hover:text-fg" to="/demo">
            Book a demo
          </Link>{" "}
          instead of starting checkout cold.
        </p>
      </Section>
    </>
  );
}
